import React from "react";

import { SMALL, useDeviceInfo } from "@swa-ui/browser";
import { Icon, Image, Link, PageGrid, Separator } from "@swa-ui/core";
import globalRules from "@swa-ui/core/assets/styles/globalRules.module.scss";
import i18n from "@swa-ui/locale";
import { HtmlValue, usePlacement } from "@swa-ui/placements";
import { useColorTheme } from "@swa-ui/theme";

import { Callouts } from "./Callouts";
import { ColumnList } from "./ColumnList";
import styles from "./DotcomDesktopFooter.module.scss";

/**
 * DotcomFooter provides the footer for each page. There are three different presentations rendered, based on screen
 * size.
 */

export const DotcomDesktopFooter = () => {
  const { placement } = usePlacement("globalNavFooter");
  const placementData = placement?.globalNavFooterLinks?.[0] ?? {};
  const globalNavCopyright = placement?.globalNavCopyright ?? {};
  const { linkColumns } = placementData;
  const { copyright, legend, logos } = globalNavCopyright;
  const { getColorName } = useColorTheme();
  const { isLargeOrXlarge, screenSize } = useDeviceInfo();
  const year = new Date().getFullYear();
  const fullCopyright = `© ${year} ${copyright?.value}`;

  return (
    <PageGrid className={styles.layout}>
      <div className={styles.calloutsAndColumns}>
        <Callouts className={styles.callouts} />
        {isLargeOrXlarge && <Separator {...getSeparatorProps()} />}
        {linkColumns && (
          <>
            <ColumnList className={styles.linkColumns} linkColumns={linkColumns} />
            {screenSize === SMALL && <Separator {...getSeparatorProps()} />}
          </>
        )}
      </div>
      {legend && (
        <div className={styles.legend}>
          <Icon name="External" size="size13" color={getColorName("footer-content")} />
          {legend?.value}
        </div>
      )}
      {copyright && (
        <div className={styles.copyright}>
          <HtmlValue htmlValue={fullCopyright} />
        </div>
      )}
      {screenSize !== SMALL && logos && (
        <div className={styles.linksAndLogo}>
          {logos.map((logo, index) => renderLogo(logo, index))}
        </div>
      )}

      {renderAriaInfoNewWindow()}
    </PageGrid>
  );

  function renderAriaInfoNewWindow() {
    return (
      <div className={globalRules.hiddenFromScreen} id="a11y-message-header--new-window">
        {i18n("DotcomDesktopFooter__NEW_WINDOW_ARIA")}
      </div>
    );
  }

  function renderLogo(logo, index) {
    const { altText, height, src, url, width } = logo;

    return (
      <Link href={url} key={`${url}-${index}`}>
        <Image alt={altText} src={src} height={height} width={width} />
      </Link>
    );
  }
  function getSeparatorProps() {
    return {
      className: styles.separator,
      color: "footer-separator",
      weight: "small",
    };
  }
};
DotcomDesktopFooter.displayName = "DotcomDesktopFooter";
